import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationContext,
  ValidationResult,
} from '../types.ts';
import { chromeArg, chromeArgs } from '../validation.ts';
import {
  errorText,
  formatError,
  result,
  stringValue,
  validation,
  validationError,
} from './shared.ts';

function glIdentity(): {
  renderer1: string;
  renderer2: string;
  masked1: string;
  masked2: string;
} {
  const read = (
    context: WebGLRenderingContext | WebGL2RenderingContext | null,
  ): { renderer: string; masked: string } => {
    if (!context) {
      return { renderer: '', masked: '' };
    }
    const extension = context.getExtension('WEBGL_debug_renderer_info');
    return {
      renderer: extension
        ? String(context.getParameter(extension.UNMASKED_RENDERER_WEBGL))
        : '',
      masked: String(context.getParameter(context.RENDERER)),
    };
  };
  const one = read(document.createElement('canvas').getContext('webgl'));
  const two = read(document.createElement('canvas').getContext('webgl2'));
  return {
    renderer1: one.renderer,
    renderer2: two.renderer,
    masked1: one.masked,
    masked2: two.masked,
  };
}

async function collect(): Promise<RawData> {
  const identity = glIdentity();
  return {
    webgl1: identity.renderer1,
    webgl2: identity.renderer2,
    maskedWebgl1: identity.masked1,
    maskedWebgl2: identity.masked2,
  };
}

function validateRenderer(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const reasons: string[] = [];
  const one = stringValue(rawData, 'webgl1') ?? '';
  const two = stringValue(rawData, 'webgl2') ?? '';
  if (!one || !two) {
    reasons.push('WebGL unmasked renderer is unavailable');
  }
  if (one && two && one != two) {
    reasons.push('WebGL 1 and WebGL 2 renderer identities differ');
  }
  if (/swiftshader|llvmpipe|mesa|headless/i.test(`${one} ${two}`)) {
    reasons.push('Renderer exposes a software or headless GPU');
  }
  const args = chromeArgs(context);
  const configured = args ? chromeArg(args, 'gl-renderer') : null;
  if (configured && !one.includes(configured) && !configured.includes(one)) {
    reasons.push('WebGL renderer does not match --gl-renderer');
  }
  return validation(reasons);
}

function rendererName(renderer: string): string {
  const angleName = /^ANGLE\s*\([^,]+,\s*([^,]+)/.exec(renderer)?.[1];
  const name = (angleName ?? renderer)
    .replace(/\s+\(0x[0-9a-f]+\).*$/i, '')
    .replace(/\s+Direct3D(?:9|11|12)?.*$/i, '')
    .trim();
  return [...name].slice(0, 32).join('') || 'Unavailable';
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  const renderer = String(rawData.webgl2 || rawData.webgl1 || 'Unavailable');
  return rendererName(renderer);
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateRenderer(rawData, context);
}

export const webGlRendererTest: TestDefinition = {
  id: 'webglRenderer',
  title: 'WebGL renderer',
  category: 'stableProfile',
  spoofingChromeArgs: ['gl-renderer'],
  format,
  run,
  validate,
};
